import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, Row, Col, Button, Table, Badge, Dropdown } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faFileExport,
    faChartBar,
    faCalendar,
    faCog,
    faDownload,
    faFilter,
    faPrint,
    faEye,
    faArrowUp,
    faArrowDown,
    faMinus
} from '@fortawesome/free-solid-svg-icons';
import { stockService } from '../../../services/StockService';

// Enregistrer les composants Chart.js
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

class StockReports extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            reportType: 'movement',
            dateRange: 'month',
            reports: {
                stockMovement: [],
                stockValue: [],
                lowStockReport: [],
                expiredStock: [],
                topProducts: [],
                costAnalysis: []
            },
            filters: {
                dateFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
                dateTo: new Date().toISOString().split('T')[0],
                stockType: 'all',
                category: 'all'
            },
            isGeneratingReport: false
        };
    }

    async componentDidMount() {
        await this.loadReportData();
    }

    loadReportData = async () => {
        this.setState({ loading: true });

        try {
            const { filters } = this.state;

            // Chargement parallèle de tous les rapports via les vraies APIs
            const [
                stockMovement,
                stockValue,
                lowStockReport,
                expiredStock,
                topProducts,
                costAnalysis
            ] = await Promise.all([
                stockService.getStockMovementReport(filters),
                stockService.getStockValueReport(filters),
                stockService.getLowStockReport(),
                stockService.getExpiredStockReport(),
                stockService.getTopProductsReport(10),
                stockService.getCostAnalysisReport(filters)
            ]);

            this.setState({
                reports: {
                    stockMovement: stockMovement.data || [],
                    stockValue: stockValue.data || [],
                    lowStockReport: lowStockReport.data || [],
                    expiredStock: expiredStock.data || [],
                    topProducts: topProducts.data || [],
                    costAnalysis: costAnalysis.data || []
                },
                loading: false
            });
        } catch (error) {
            console.error('Erreur lors du chargement des rapports:', error);

            // En cas d'erreur, initialiser avec des tableaux vides
            this.setState({
                reports: {
                    stockMovement: [],
                    stockValue: [],
                    lowStockReport: [],
                    expiredStock: [],
                    topProducts: [],
                    costAnalysis: []
                },
                loading: false
            });

            // Optionnel: Afficher une notification d'erreur
            if (this.props.showErrorNotification) {
                this.props.showErrorNotification('Erreur lors du chargement des rapports');
            }
        }
    };

    generateReport = async () => {
        this.setState({ isGeneratingReport: true });

        try {
            // Recharger les données avec les filtres actuels
            await this.loadReportData();

            console.log('Rapport généré avec les filtres:', this.state.filters);
            this.setState({ isGeneratingReport: false });
        } catch (error) {
            console.error('Erreur lors de la génération du rapport:', error);
            this.setState({ isGeneratingReport: false });
        }
    };

    exportReport = async (format) => {
        try {
            const { reportType, filters } = this.state;

            if (format === 'excel') {
                await stockService.downloadReportExcel(reportType, filters);
            } else {
                console.log(`Export en format ${format} non implémenté`);
            }
        } catch (error) {
            console.error('Erreur lors de l\'export:', error);
            if (this.props.showErrorNotification) {
                this.props.showErrorNotification('Erreur lors de l\'export du rapport');
            }
        }
    };

    handleFilterChange = (field, value) => {
        this.setState({
            filters: {
                ...this.state.filters,
                [field]: value
            }
        });
    };

    getMovementIcon = (type) => {
        switch (type) {
            case 'IN':
                return <FontAwesomeIcon icon={faArrowUp} className="text-success" />;
            case 'OUT':
                return <FontAwesomeIcon icon={faArrowDown} className="text-danger" />;
            default:
                return <FontAwesomeIcon icon={faMinus} className="text-info" />;
        }
    };

    getStockChartData = () => {
        const { reports } = this.state;

        return {
            labels: reports.stockValue.map(item => item.category),
            datasets: [{
                label: 'Valeur Stock (FCFA)',
                data: reports.stockValue.map(item => item.totalValue),
                backgroundColor: [
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(255, 99, 132, 0.6)',
                    'rgba(255, 206, 86, 0.6)',
                    'rgba(75, 192, 192, 0.6)'
                ],
                borderColor: [
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 99, 132, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)'
                ],
                borderWidth: 1
            }]
        };
    };

    getTopProductsChartData = () => {
        const { reports } = this.state;

        return {
            labels: reports.topProducts.map(item => item.productName),
            datasets: [{
                data: reports.topProducts.map(item => item.percentage),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0'
                ]
            }]
        };
    };

    render() {
        const { loading, reports, filters, isGeneratingReport, reportType } = this.state;

        if (loading) {
            return (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Chargement...</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="stock-reports">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="text-primary">
                        <FontAwesomeIcon icon={faChartBar} className="me-2" />
                        Rapports & Analytics
                    </h4>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            onClick={this.generateReport}
                            disabled={isGeneratingReport}
                        >
                            {isGeneratingReport ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Génération...
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faFileExport} className="me-2" />
                                    Générer Rapport
                                </>
                            )}
                        </Button>
                        <Dropdown>
                            <Dropdown.Toggle variant="success" size="sm">
                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                Exporter
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => this.exportReport('pdf')}>
                                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                                    PDF
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => this.exportReport('excel')}>
                                    <FontAwesomeIcon icon={faFileExport} className="me-2" />
                                    Excel
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => this.exportReport('csv')}>
                                    <FontAwesomeIcon icon={faFileExport} className="me-2" />
                                    CSV
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                {/* Filtres */}
                <Card className="mb-4">
                    <Card.Header>
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        Filtres de Rapport
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <label className="form-label">Date de début</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.dateFrom}
                                    onChange={(e) => this.handleFilterChange('dateFrom', e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <label className="form-label">Date de fin</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.dateTo}
                                    onChange={(e) => this.handleFilterChange('dateTo', e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <label className="form-label">Type de Stock</label>
                                <select
                                    className="form-control"
                                    value={filters.stockType}
                                    onChange={(e) => this.handleFilterChange('stockType', e.target.value)}
                                >
                                    <option value="all">Tous les types</option>
                                    <option value="blank_cards">Cartes Vierges</option>
                                    <option value="ribbons">Rubans</option>
                                    <option value="laminates">Films</option>
                                </select>
                            </Col>
                            <Col md={3}>
                                <label className="form-label">Catégorie</label>
                                <select
                                    className="form-control"
                                    value={filters.category}
                                    onChange={(e) => this.handleFilterChange('category', e.target.value)}
                                >
                                    <option value="all">Toutes les catégories</option>
                                    <option value="production">Production</option>
                                    <option value="maintenance">Maintenance</option>
                                    <option value="quality">Qualité</option>
                                </select>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Row>
                    {/* Graphique de la valeur du stock */}
                    <Col lg={6} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h5>Valeur du Stock par Catégorie</h5>
                            </Card.Header>
                            <Card.Body>
                                <Bar data={this.getStockChartData()} options={{
                                    responsive: true,
                                    plugins: {
                                        legend: {
                                            position: 'top'
                                        },
                                        title: {
                                            display: true,
                                            text: 'Répartition de la Valeur'
                                        }
                                    }
                                }} />
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Graphique des produits les plus utilisés */}
                    <Col lg={6} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h5>Produits les Plus Utilisés</h5>
                            </Card.Header>
                            <Card.Body>
                                <Doughnut data={this.getTopProductsChartData()} options={{
                                    responsive: true,
                                    plugins: {
                                        legend: {
                                            position: 'bottom'
                                        }
                                    }
                                }} />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* Mouvements de Stock */}
                    <Col lg={8} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h5>Derniers Mouvements de Stock</h5>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Produit</th>
                                            <th>Type</th>
                                            <th>Quantité</th>
                                            <th>Raison</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports.stockMovement.map(movement => (
                                            <tr key={movement.id}>
                                                <td>{new Date(movement.date).toLocaleDateString()}</td>
                                                <td>
                                                    <div>
                                                        <strong>{movement.productCode}</strong>
                                                        <br />
                                                        <small className="text-muted">{movement.productName}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    {this.getMovementIcon(movement.type)}
                                                    <span className="ms-1">
                                                        {movement.type === 'IN' ? 'Entrée' : 'Sortie'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${movement.type === 'IN' ? 'bg-success' : 'bg-danger'}`}>
                                                        {Math.abs(movement.quantity)}
                                                    </span>
                                                </td>
                                                <td>{movement.reason}</td>
                                                <td>
                                                    <Button size="sm" variant="outline-primary">
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Stocks Faibles */}
                    <Col lg={4} className="mb-4">
                        <Card>
                            <Card.Header>
                                <h5>Alertes Stock Faible</h5>
                            </Card.Header>
                            <Card.Body>
                                {reports.lowStockReport.map((item, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 p-2 border-bottom">
                                        <div>
                                            <strong>{item.productCode}</strong>
                                            <br />
                                            <small className="text-muted">{item.productName}</small>
                                        </div>
                                        <div className="text-end">
                                            <Badge bg={item.status === 'critical' ? 'danger' : 'warning'}>
                                                {item.currentStock}/{item.minimumThreshold}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    stocks: state.stockManagement?.stocks || [],
    loading: state.stockManagement?.loading || false,
    error: state.stockManagement?.error
});

export default connect(mapStateToProps)(StockReports);